import { LineItem } from 'types/parts-diagram';
import { MAX_ZOOM } from './constants';

const createCircleElement = (
  cx: number,
  fill: string,
  stroke: string,
  textElement: SVGTextElement,
  className = '',
  onClick?: () => void
): SVGCircleElement => {
  const circle = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'circle'
  );
  circle.setAttribute('cx', cx.toString());
  circle.setAttribute('cy', '5');
  circle.setAttribute('r', '8');
  circle.setAttribute('fill', fill);
  circle.setAttribute('stroke', stroke);
  circle.setAttribute('transform', textElement.getAttribute('transform') ?? '');
  if (onClick) {
    circle.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
  }
  if (className) {
    circle.classList.add(className);
  }
  return circle;
};

const addCircleToTextElement = (
  textElement: SVGTextElement,
  fill: string,
  stroke: string,
  className = '',
  onClick?: () => void
) => {
  const textLength = textElement.textContent.length;
  const textContent = textElement.textContent?.trim() || '';

  if (textContent === '-' || textContent === '') {
    return;
  }

  const cx = 3 * textLength;
  const circle = createCircleElement(
    cx,
    fill,
    stroke,
    textElement,
    className,
    onClick
  );
  textElement.parentNode?.insertBefore(circle, textElement);
};

export const addDefaultCircles = (
  svgDoc: Element,
  referenceNumber: string,
  onClick?: () => void
) => {
  if (referenceNumber === null || referenceNumber === undefined) return;
  const svg = svgDoc.querySelector('svg #CanvasCalloutsGP');
  if (!svg) return;

  const textElements = svg.querySelectorAll(
    `[data-callout="${referenceNumber}"] text, [id^=ID_${referenceNumber}_] > text`
  );
  textElements.forEach((textElement) => {
    const x = textElement.getAttribute('x');
    const y = textElement.getAttribute('y');
    if (x && y) {
      if (textElement instanceof SVGTextElement) {
        addCircleToTextElement(
          textElement,
          'rgba(255, 255, 255, 0.05)',
          '#000',
          'default-circle',
          onClick
        );
      }
    }
  });
};

export const highlightDiagram = (svgDoc: Element, hoveredItem: string) => {
  if (hoveredItem === null || hoveredItem === undefined) return;
  const svg = svgDoc.querySelector('svg #CanvasCalloutsGP');
  if (!svg) return;

  const textElements = svg.querySelectorAll(
    `[data-callout="${hoveredItem}"] text, [id^=ID_${hoveredItem}_] > text`
  );
  textElements.forEach((textElement) => {
    const x = textElement.getAttribute('x');
    const y = textElement.getAttribute('y');
    if (x && y) {
      if (textElement instanceof SVGTextElement) {
        textElement.setAttribute('fill', 'white');
        addCircleToTextElement(textElement, '#000', '#000', 'highlight');
      }
    }
  });
};

export const getDragBounds = (height = 0, width = 0, zoom = 1) => {
  const originalWidth = width / zoom;
  const right = 0.5 * (width - originalWidth);
  const left = -1 * right;

  const originalHeight = height / zoom;
  const bottom = 0.5 * (height - originalHeight);
  const top = -1 * bottom;
  return { bottom, left, right, top };
};

export const diagramZoom = (svgDoc: HTMLElement, zoom: number) => {
  const transform = `scale3d(${zoom}, ${zoom}, ${zoom})`;
  svgDoc.style.transform = transform;
};

export const isValidZoom = (newZoom: number) =>
  newZoom >= 1 && newZoom <= MAX_ZOOM;

export const reOrderPartItems = (
  lineItems: LineItem[],
  givenItem: string
): LineItem[] => {
  const removedItems = lineItems.filter(
    (item) => item?.referenceNumber === givenItem
  );
  const remainingItems = lineItems.filter(
    (item) => item?.referenceNumber !== givenItem
  );
  const sortedRemainingItems = [...remainingItems].sort(
    (a: LineItem, b: LineItem) =>
      Number(a.referenceNumber) - Number(b.referenceNumber)
  );

  const reOrderedParts = [...removedItems, ...sortedRemainingItems];
  return reOrderedParts;
};
export const countValidParts = (arr: LineItem[]) => {
  return arr.filter((item) => item.name).length;
};
